import React, { useState, useContext, useEffect } from 'react';
import Joyride, { CallBackProps, Step, TooltipRenderProps } from 'react-joyride';
import { Button } from '@/components/ui/button';
import { HelpModalContext } from '../Layout/MainLayout';
import { useNavigate } from 'react-router-dom';

interface PlatformTourProps {
  onFinish: () => void;
  onSkip: () => void;
  role: string;
}

const getTourSteps = (role: string, onHelpClick: () => void): Step[] => {
  // Common steps for all roles - using more reliable selectors
  const steps: Step[] = [
    {
      target: '#main-sidebar',
      title: 'Main Navigation',
      content: 'Use the sidebar to access all major modules: Dashboard, Data Management, Carbon Accounting, and more.',
      disableBeacon: true,
    },
    {
      target: 'body', // Use body as fallback for dashboard
      title: 'Dashboard Overview',
      content: 'Your dashboard provides real-time ESG metrics, performance indicators, and smart insights. Navigate to the Dashboard to see your ESG performance overview.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for data management
      title: 'Data Management',
      content: 'Manage all your ESG data here. Navigate to Data Management to add, import, or edit data points. Try uploading a CSV or adding a new metric.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for carbon accounting
      title: 'Carbon Accounting',
      content: 'Track your carbon footprint and set net-zero targets. Navigate to Carbon Accounting to use calculators for energy, production, and waste.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for risk management
      title: 'Risk Management',
      content: 'Assess and manage ESG and climate risks. Navigate to Risk Management to use scenario analysis and mitigation planning tools.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for supply chain
      title: 'Supply Chain',
      content: 'Monitor supplier ESG performance and manage your supply chain. Navigate to Supply Chain to track supplier performance.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for reporting
      title: 'ESG Reporting',
      content: 'Generate standards-compliant reports (GRI, SASB, TCFD) and export data in multiple formats. Navigate to ESG Reporting for comprehensive reporting.',
      placement: 'center',
    },
    {
      target: 'body', // Use body as fallback for settings
      title: 'Settings',
      content: 'Manage your profile, notification preferences, and storage settings here. Navigate to Settings to customize your experience.',
      placement: 'center',
    },
    {
      target: '#help-menu',
      title: 'Help & Support',
      content: (
        <div>
          Access help, documentation, and support at any time.<br />
          <Button onClick={onHelpClick} className="mt-2 glassy-btn">Open Help & Support</Button>
        </div>
      ),
    },
  ];

  // Role-based customization
  if (role === 'admin' || role === 'super_admin') {
    steps.splice(7, 0, {
      target: 'body', // Use body as fallback for organization management
      title: 'Organization Management',
      content: 'Invite and manage team members, assign roles, and configure organization settings. Navigate to Settings to manage your team.',
      placement: 'center',
    });
  }
  if (role === 'executive') {
    // Emphasize dashboard and reporting
    steps.splice(1, 0, {
      target: 'body',
      title: 'Executive Dashboard',
      content: 'Get a high-level overview of your organization\'s ESG performance and key insights. Navigate to Dashboard for executive-level metrics.',
      placement: 'center',
    });
  }
  if (role === 'data_entry' || role === 'analyst') {
    // Emphasize data entry and analytics
    steps.splice(2, 0, {
      target: 'body', // Use body as fallback for quick metric entry
      title: 'Quick Metric Entry',
      content: 'Use quick entry forms to rapidly add new ESG data points. Navigate to Data Management to find the Quick Metric Entry section.',
      placement: 'center',
    });
  }
  return steps;
};

const GlassyTooltip = (props: TooltipRenderProps) => {
  const { step, index, isLastStep, size, backProps, skipProps, primaryProps } = props;
  const navigate = useNavigate();
  
  // Function to get the correct path based on step title
  const getPathForStep = (stepTitle: string) => {
    const pathMap: { [key: string]: string } = {
      'Dashboard Overview': '/',
      'Executive Dashboard': '/',
      'Data Management': '/data-management',
      'Quick Metric Entry': '/data-management',
      'Carbon Accounting': '/carbon-accounting',
      'Risk Management': '/risk-management',
      'Supply Chain': '/supply-chain',
      'ESG Reporting': '/reporting',
      'Settings': '/settings',
      'Organization Management': '/settings',
    };
    return pathMap[stepTitle] || '/';
  };
  
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  
  return (
    <div
      className="backdrop-blur-md bg-white/70 border border-blue-200 shadow-xl rounded-2xl p-6 animate-fade-in"
      style={{
        minWidth: 320,
        maxWidth: 400,
        color: '#1e293b',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        border: '1px solid rgba(255,255,255,0.18)',
        transition: 'all 0.3s cubic-bezier(.4,2,.6,1)',
      }}
      aria-live="polite"
    >
      <div className="font-bold text-lg mb-2 text-blue-700 animate-slide-down">{step.title}</div>
      <div className="mb-4 text-base animate-fade-in-slow">{step.content}</div>
      
      {/* Navigation button for relevant steps */}
      {index > 0 && step.title !== 'Main Navigation' && step.title !== 'Help & Support' && (
        <div className="mb-4">
          <Button 
            onClick={() => handleNavigate(getPathForStep(step.title))}
            className="w-full glassy-btn"
            variant="outline"
          >
            Navigate to {step.title}
          </Button>
        </div>
      )}
      
      <div className="flex justify-between items-center mt-4 gap-2">
        {index > 0 && (
          <Button {...backProps} variant="outline" className="glassy-btn">Back</Button>
        )}
        <div className="flex gap-2 ml-auto">
          {skipProps && (
            <Button {...skipProps} variant="ghost" className="glassy-btn">Skip Tour</Button>
          )}
          <Button {...primaryProps} className="glassy-btn">
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PlatformTour: React.FC<PlatformTourProps> = ({ onFinish, onSkip, role }) => {
  const [run, setRun] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const helpModal = useContext(HelpModalContext);

  const steps = getTourSteps(role, helpModal.open);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, type } = data;
    
    if (["finished", "skipped"].includes(status)) {
      setRun(false);
      if (status === "finished") onFinish();
      if (status === "skipped") onSkip();
    } else if (type === "step:after") {
      setStepIndex(index + 1);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      disableOverlayClose
      tooltipComponent={GlassyTooltip}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#2563eb',
          textColor: '#222',
          overlayColor: 'rgba(30,41,59,0.35)',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      callback={handleJoyrideCallback}
      spotlightClicks={false} // Disable spotlight clicks to prevent navigation issues
      disableScrolling={false}
      scrollToFirstStep
      disableOverlay={false}
      floaterProps={{
        disableAnimation: true,
      }}
    />
  );
}; 